import {
  UpdatePresentationPlanningOpportunitiesBundleConfigurationInput,
  UpdatePresentationPlanningOpportunitiesItemInput,
} from '@/types/schema';

import { NEW_ROW_ID_SENTINEL } from '../../ClientPresentationDesigner.constants';
import {
  isEmptyKeyedListMutationOperations,
  mapKeyedListMutationOperations,
} from '../diff/mapKeyedListMutationOperations';
import {
  BundleConfigurationPayload,
  DiffBundleConfigurationArgs,
} from '../presentationSave.types';

function serverRowId(row: { id: string }): string | null {
  return row.id.startsWith(NEW_ROW_ID_SENTINEL) ? null : row.id;
}

export function diffPlanningOpportunitiesBundleConfiguration({
  previous,
  next,
  context,
}: DiffBundleConfigurationArgs): BundleConfigurationPayload | null {
  const before = previous.planningOpportunitiesBundleConfiguration;
  const after = next.planningOpportunitiesBundleConfiguration;
  if (!before || !after?.id) return null;

  const update: UpdatePresentationPlanningOpportunitiesBundleConfigurationInput =
    {};
  if (before.displayName !== after.displayName) {
    if (after.displayName) {
      update.displayName = after.displayName;
    } else {
      update.clearDisplayName = true;
    }
  }

  const wasAt = new Map(
    before.opportunities.map((row, index) => [row.id, index])
  );

  const rows = mapKeyedListMutationOperations({
    previousItems: before.opportunities,
    currentItems: after.opportunities,
    getId: serverRowId,
    toCreate: (row, sortOrder) => ({
      create: {
        householdID: context.householdId,
        opportunityLabel: row.opportunityLabel,
        opportunityBody: row.opportunityBody,
        rationaleLabel: row.rationaleLabel,
        rationaleBody: row.rationaleBody,
        nextStepLabel: row.nextStepLabel,
        nextStepBody: row.nextStepBody,
        sortOrder,
      },
    }),
    toUpdate: ({ previous: was, next: is, sortOrder }) => {
      const row: UpdatePresentationPlanningOpportunitiesItemInput = {};
      if (was.opportunityLabel !== is.opportunityLabel) {
        row.opportunityLabel = is.opportunityLabel;
      }
      if (was.opportunityBody !== is.opportunityBody) {
        row.opportunityBody = is.opportunityBody;
      }
      if (was.rationaleLabel !== is.rationaleLabel) {
        row.rationaleLabel = is.rationaleLabel;
      }
      if (was.rationaleBody !== is.rationaleBody) {
        row.rationaleBody = is.rationaleBody;
      }
      if (was.nextStepLabel !== is.nextStepLabel) {
        row.nextStepLabel = is.nextStepLabel;
      }
      if (was.nextStepBody !== is.nextStepBody) {
        row.nextStepBody = is.nextStepBody;
      }
      if (wasAt.get(is.id) !== sortOrder) row.sortOrder = sortOrder;
      return Object.keys(row).length > 0 ? { id: is.id, update: row } : null;
    },
  });

  if (rows.removeIDs.length > 0) update.removeItemIDs = rows.removeIDs;
  if (
    Object.keys(update).length === 0 &&
    isEmptyKeyedListMutationOperations(rows)
  ) {
    return null;
  }

  return {
    updatePlanningOpportunitiesBundleConfiguration: {
      id: after.id,
      update,
      ...(rows.creates.length > 0 ? { withItems: rows.creates } : {}),
      ...(rows.updates.length > 0 ? { updateItems: rows.updates } : {}),
    },
  };
}
